import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Search, ChevronDown, Phone, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { SiteSearch } from "./SiteSearch";
import { SocialLinks } from "./Social";
import { scrollToId } from "@/lib/scroll";
import { useT, useLang, LangSwitch } from "@/i18n";
import { WHITE_THEME } from "@/lib/theme";

export const LINKS = [
  ["Prodotti", "#prodotti"],
  ["Legno lamellare", "/legno-lamellare"],
  ["Pannelli X-LAM", "/pannelli-xlam"],
  ["Lettiere per animali", "/lettiere-animali"],
  ["Biomassa", "/biomassa"],
  ["Guida tecnica", "/guida-tecnica"],
  ["Chi siamo", "/chi-siamo"],
  ["Perché noi", "#perche-emma"],
  ["La casa in legno", "#casa-in-legno"],
  ["Balneari", "#balneari"],
  ["Progetti", "#progetti"],
  ["Cantieri", "#cantieri"],
  ["Enti pubblici", "/enti-pubblici"],
  ["Certificazioni", "#certificazioni"],
  ["Sostenibilità", "#sostenibilita"],
  ["Contatti", "#contatti"],
];

// Main menu (6 entries); "Prodotti" opens the product pages.
const SUB = [
  ["Legno lamellare", "/legno-lamellare", "nav.legno-lamellare"],
  ["Pannelli X-LAM", "/pannelli-xlam", "nav.pannelli-xlam"],
  ["Lettiere per animali", "/lettiere-animali", "nav.lettiere-animali"],
  ["Biomassa", "/biomassa", "nav.biomassa"],
  ["Guida tecnica", "/guida-tecnica", "nav.guida-tecnica"],
  ["Tutti i prodotti", "#prodotti", "nav.allProducts"],
];
// Structural calculators: the ?calc= value opens that tab of SectionCalc.
const CALCS = [
  ["Trave appoggiata", "/legno-lamellare?calc=beam#calcolo", "calc.m.beam"],
  ["Solaio", "/legno-lamellare?calc=floor#calcolo", "calc.m.floor"],
  ["Copertura a falde", "/legno-lamellare?calc=roof#calcolo", "calc.m.roof"],
  ["Solaio X-LAM", "/pannelli-xlam?calc=xlam#calcolo", "calc.m.xlam"],
  ["Tutti i calcolatori", "/calcolatori", "nav.allCalcs"],
];
export const MENU = [
  ["Prodotti", "#prodotti", "nav.prodotti", SUB],
  ["Calcolatori", "/calcolatori", "nav.calcolatori", CALCS],
  ["La casa in legno", "#cap-case", "nav.casa-in-legno"],
  ["Balneari", "/stabilimenti-balneari", "nav.balneari"],
  ["Progetti", "#progetti", "nav.progetti"],
  ["Chi siamo", "/chi-siamo", "nav.chi-siamo"],
  ["Contatti", "#contatti", "nav.contatti"],
];
const slug = (to) => to.replace(/[?=]/g, "-").replace(/[^a-z-]/g, "");

// Thin top bar: quick contacts, socials and language.
const TopBar = ({ hidden }) => (
  <div className={`keep-dark overflow-hidden bg-[#121110] text-[#F7F5F0] transition-[max-height,opacity] duration-500 ${hidden ? "max-h-0 opacity-0" : "max-h-12 opacity-100"}`} data-testid="top-bar">
    <div className="mx-auto flex h-10 max-w-[1500px] items-center justify-between gap-4 px-5 font-mono text-xs sm:px-8">
      <div className="flex items-center gap-4 sm:gap-6">
        <a href="tel:+390510830043" className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-[#D98A52]" data-testid="top-bar-phone"><Phone className="h-3.5 w-3.5" /><span className="hidden sm:inline">051 0830043</span></a>
        <a href="mailto:info@emmaindustrie.it" className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-[#D98A52]" data-testid="top-bar-email"><Mail className="h-3.5 w-3.5" /><span className="hidden md:inline">info@emmaindustrie.it</span></a>
        <a href="https://wa.me/393803758249" target="_blank" rel="noopener noreferrer" className="transition-colors duration-300 hover:text-[#D98A52]" data-testid="top-bar-whatsapp">WhatsApp<span className="hidden lg:inline"> 380 3758249</span></a>
      </div>
      <div className="flex items-center gap-3">
        <SocialLinks id="top-social" className="[&_a]:h-7 [&_a]:w-7 [&_svg]:h-3.5 [&_svg]:w-3.5" btnClass="border-white/20" />
        <LangSwitch className="hidden scale-90 sm:flex" />
      </div>
    </div>
  </div>
);

const MenuItem = ({ item, t }) => {
  const [label, to, key, sub] = item;
  if (!sub) return <li><NavA to={to} className="nav-link whitespace-nowrap font-mono text-xs uppercase tracking-[0.08em] 2xl:tracking-[0.12em]" data-testid={`nav-link-${slug(to)}`}>{t(key, label)}</NavA></li>;
  return (
    <li className="group relative">
      <NavA to={to} className="nav-link inline-flex items-center gap-1 whitespace-nowrap font-mono text-xs uppercase tracking-[0.08em] 2xl:tracking-[0.12em]" aria-haspopup="true" data-testid={`nav-link-${slug(to)}`}>
        {t(key, label)} <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-focus-within:rotate-180 group-hover:rotate-180" />
      </NavA>
      <div className="invisible absolute left-1/2 top-full z-10 -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-300 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100" data-testid="nav-products-menu">
        <ul className="min-w-[240px] rounded-3xl border border-white/10 bg-[#121110] p-2 shadow-2xl">
          {sub.map(([l, href, k]) => (
            <li key={href}><NavA to={href} className="block rounded-full px-4 py-2.5 text-sm transition-colors duration-300 hover:bg-white/10 hover:text-[#D98A52]" data-testid={`nav-sub-${slug(href)}`}>{t(k, l)}</NavA></li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (id.startsWith("/")) return navigate(id);
    if (pathname !== "/") return navigate(`/${id}`);
    return scrollToId(id);
  };
};

// Real link (crawlable href, keeps the language) that still scrolls smoothly / routes in-app on click.
export const NavA = ({ to, onGo, children, ...rest }) => {
  const go = useGo();
  const { lang } = useLang();
  const [path, hash = ""] = (to.startsWith("#") ? `/${to}` : to).split("#");
  const href = `${path}${lang === "it" ? "" : `${path.includes("?") ? "&" : "?"}lang=${lang}`}${hash ? `#${hash}` : ""}`;
  const onClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onGo?.();
    go(to);
  };
  return <a href={href} onClick={onClick} {...rest}>{children}</a>;
};

export const Nav = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchStart, setSearchStart] = useState(null);
  useEffect(() => {
    const onOpen = (e) => { setSearchStart({ ...(e?.detail ?? {}) }); setSearching(true); };
    window.addEventListener("open-search", onOpen);
    return () => window.removeEventListener("open-search", onOpen);
  }, []);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50"
        data-testid="site-header"
      >
        <TopBar hidden={scrolled} />
        <div className="px-4 pt-3 sm:px-8">
        <nav className={`keep-dark mx-auto flex max-w-[1500px] items-center justify-between rounded-full border border-white/10 bg-[#121110]/95 px-4 text-[#F7F5F0] backdrop-blur-xl transition-[padding] duration-500 sm:px-6 ${scrolled ? "py-1.5" : "py-2"}`} data-testid="nav-bar">
          <NavA to="#top" className="shrink-0" data-testid="nav-logo" aria-label="emma industrie home">
            <Logo className={`transition-[height] duration-500 ease-out ${scrolled ? "h-11 sm:h-12" : "h-12 sm:h-16"}`} />
          </NavA>
          <ul className="mx-4 hidden items-center gap-4 xl:flex 2xl:gap-7" data-testid="nav-menu">
            {MENU.map((item) => <MenuItem key={item[1]} item={item} t={t} />)}
          </ul>
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden sm:block">
              <NavA to="#contatti" className="btn-amber" data-testid="nav-cta-preventivo">
                {t("nav.quote", "Preventivo")} <ArrowUpRight className="h-4 w-4" />
              </NavA>
            </span>
            <button onClick={() => setSearching(true)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 transition-colors duration-300 hover:border-[#D98A52]" data-testid="nav-search-open" aria-label={t("search.title", "Cerca nel sito")}>
              <Search className="h-5 w-5" />
            </button>
            <button onClick={() => setOpen(true)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 xl:hidden" data-testid="nav-menu-open" aria-label="Apri menu">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
        </div>
      </motion.header>
      <SiteSearch open={searching} onOpenChange={(v) => { setSearching(v); if (!v) setSearchStart(null); }} start={searchStart} />
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[80] flex flex-col bg-[#121110] px-6 pb-10 pt-6 text-[#F7F5F0]"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo tone={WHITE_THEME ? "dark" : "light"} className="h-10 sm:h-[52px]" />
              <button onClick={() => setOpen(false)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15" data-testid="nav-menu-close" aria-label="Chiudi menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="mt-10 space-y-1 overflow-y-auto">
              {MENU.map(([label, to, key, sub], i) => (
                <motion.li key={to} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 + i * 0.06 }}>
                  <NavA to={to} onGo={() => setOpen(false)} className="font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid={`mobile-link-${slug(to)}`}>
                    {t(key, label)}
                  </NavA>
                  {sub && (
                    <ul className="mb-3 mt-2 flex flex-wrap gap-2">
                      {(sub === SUB ? sub.slice(0, -1) : sub).map(([l, href, k]) => (
                        <li key={href}><NavA to={href} onGo={() => setOpen(false)} className="inline-block rounded-full border border-white/20 px-3 py-1.5 text-sm" data-testid={`mobile-sub-${slug(href)}`}>{t(k, l)}</NavA></li>
                      ))}
                    </ul>
                  )}
                </motion.li>
              ))}
            </ul>
            <LangSwitch className="mt-10 w-fit" />
            <NavA to="#contatti" onGo={() => setOpen(false)} className="btn-amber mt-auto justify-center" data-testid="mobile-cta-preventivo">
              {t("nav.quoteLong", "Richiedi preventivo")} <ArrowUpRight className="h-4 w-4" />
            </NavA>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
